<script setup lang="ts">
import type {
  DocumentDownloadFormat,
  LibrarySearchGroup,
} from '@/js/account/entities/document';
import { computed, useId } from 'vue';
import { RouterLink } from 'vue-router';
import { AccountRouteName } from '@/js/account/app/router/route-names';
import ActionButton from '@/js/account/components/base/ActionButton.vue';
import BaseChip from '@/js/account/components/base/BaseChip.vue';
import BaseIcon from '@/js/account/components/base/BaseIcon.vue';
import DownloadFormatMenu from '@/js/account/components/document/DownloadFormatMenu.vue';
import LibrarySearchSnippet from '@/js/account/components/library/search/LibrarySearchSnippet.vue';
import {
  getDocumentDownloadFormats,
  LIBRARY_SEARCH_SNIPPETS_PREVIEW,
} from '@/js/account/entities/document';
import { classNames } from '@/js/lib/bem';

const props = withDefaults(
  defineProps<{
    group: LibrarySearchGroup
    open: boolean
    expanded: boolean
    downloading?: boolean
    deleting?: boolean
    showAdminActions?: boolean
  }>(),
  { downloading: false, deleting: false, showAdminActions: false },
);

const emit = defineEmits<{
  toggle: []
  expand: []
  download: [format: DocumentDownloadFormat]
  delete: []
}>();

const cn = classNames('library-search-document-group');

const headerId = useId();
const panelId = useId();

const documentRoute = computed(() => ({
  name: AccountRouteName.LibraryDocument,
  params: { id: String(props.group.document.id) },
}));

const downloadFormats = computed(() =>
  getDocumentDownloadFormats(props.group.document),
);

const visibleMatches = computed(() =>
  props.expanded
    ? props.group.matches
    : props.group.matches.slice(0, LIBRARY_SEARCH_SNIPPETS_PREVIEW),
);

const hiddenCount = computed(
  () => props.group.matches.length - visibleMatches.value.length,
);

const matchesLabel = computed(() => {
  const total = props.group.matchesTotal;
  const noun = total === 1 ? 'match' : 'matches';

  return props.group.truncated ? `${total}+ ${noun}` : `${total} ${noun}`;
});

const editRoute = computed(() => ({
  name: AccountRouteName.AdminDocumentEdit,
  params: { id: String(props.group.document.id) },
}));

const editLabel = computed(() => `Edit ${props.group.document.title}`);
const deleteLabel = computed(() => `Delete ${props.group.document.title}`);

function onDelete(): void {
  if (props.deleting) {
    return;
  }

  emit('delete');
}
</script>

<template>
  <section :class="cn()">
    <div :class="cn('header')">
      <button
        :id="headerId"
        type="button"
        :class="cn('toggle')"
        :aria-expanded="open"
        :aria-controls="panelId"
        @click="emit('toggle')"
      >
        <BaseIcon
          name="left"
          :width="16"
          :height="16"
          :class="cn('chevron', { open })"
        />
        <span :class="cn('title')">{{ group.document.title }}</span>
        <BaseChip v-if="group.matchesTotal > 0" theme="primary" size="sm">
          {{ matchesLabel }}
        </BaseChip>
      </button>
      <DownloadFormatMenu
        v-if="downloadFormats.length > 0"
        :document-title="group.document.title"
        :formats="downloadFormats"
        :disabled="downloading"
        placement="left-start"
        @download="emit('download', $event)"
      />
      <RouterLink
        v-if="showAdminActions"
        v-slot="{ navigate }"
        :to="editRoute"
        custom
      >
        <div :class="cn('action', { disabled: deleting })">
          <ActionButton
            icon="edit"
            :label="editLabel"
            :disabled="deleting"
            @click="navigate"
          />
        </div>
      </RouterLink>
      <div
        v-if="showAdminActions"
        :class="cn('action', { disabled: deleting })"
      >
        <ActionButton
          icon="trash"
          :label="deleteLabel"
          :disabled="deleting"
          @click="onDelete"
        />
      </div>
    </div>

    <div
      v-show="open"
      :id="panelId"
      role="region"
      :aria-labelledby="headerId"
      :class="cn('panel')"
    >
      <div v-if="group.document.tags.length > 0" :class="cn('tags')">
        <BaseChip
          v-for="tag in group.document.tags"
          :key="tag.id"
          theme="soft"
          size="sm"
        >
          {{ tag.name }}
        </BaseChip>
      </div>

      <ol v-if="visibleMatches.length > 0" :class="cn('matches')">
        <LibrarySearchSnippet
          v-for="match in visibleMatches"
          :key="match.key"
          :match="match"
          :number="match.numberInDocument"
        />
      </ol>
      <p v-else :class="cn('empty')">
        Matched in the title only.
        <RouterLink :to="documentRoute" :class="cn('empty-link')">
          Open document →
        </RouterLink>
      </p>

      <button
        v-if="hiddenCount > 0"
        type="button"
        :class="cn('more')"
        @click="emit('expand')"
      >
        Show {{ hiddenCount }} more
        {{ hiddenCount === 1 ? 'match' : 'matches' }} in this document
      </button>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.library-search-document-group {
  border-radius: 4px;
  background-color: var(--color-white);
  box-shadow: var(--shadow);
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 12px 12px 16px;
  }

  &__toggle {
    display: flex;
    flex: 1;
    align-items: center;
    gap: 10px;
    min-width: 0;
    padding: 4px 0;
    border: none;
    background: none;
    color: inherit;
    font-family: inherit;
    text-align: left;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }

  &__action {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 4px;
    transition: background-color 0.2s ease;

    &:hover:not(&_disabled) {
      background-color: var(--color-background-primary);
    }

    &_disabled {
      opacity: 0.6;
    }
  }

  &__chevron {
    color: var(--color-text-secondary);
    transform: rotate(180deg);
    transition: transform 0.2s ease;

    &_open {
      transform: rotate(-90deg);
    }
  }

  &__title {
    flex: 1;
    min-width: 0;
    color: var(--color-text-primary);
    font-size: 16px;
    font-weight: var(--font-weight-medium);
    line-height: 1.2;
    overflow-wrap: anywhere;
  }

  &__panel {
    display: flex;
    flex-direction: column;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 0 16px 12px;
  }

  &__matches {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__empty {
    margin: 0;
    padding: 0 16px 16px;
    color: var(--color-text-secondary);
    font-size: 14px;
  }

  &__empty-link {
    color: var(--color-primary);
    font-weight: var(--font-weight-medium);
    text-decoration: none;
  }

  &__more {
    padding: 12px 16px;
    border: none;
    border-top: 1px solid var(--color-background-secondary);
    background: none;
    color: var(--color-primary);
    font-family: inherit;
    font-size: 13px;
    font-weight: var(--font-weight-medium);
    text-align: left;
    cursor: pointer;

    &:hover {
      color: var(--color-primary-dark);
    }

    &:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: -2px;
    }
  }

  @media (max-width: 768px) {
    &__header {
      flex-wrap: wrap;
      padding: 12px;
    }

    &__more {
      min-height: 44px;
    }
  }
}
</style>
